import React, { FC } from 'react';
import { Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { ChatV2QuickActionCategory } from '../types';

import './QuickActionList.scss';

interface QuickActionListProps {
  categories: ChatV2QuickActionCategory[];
  onCategoryPress: (category: ChatV2QuickActionCategory) => void;
}

const fadeInDownVariants = {
  initial: {
    opacity: 0,
    y: -10,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
};

const QuickActionList: FC<QuickActionListProps> = ({ categories, onCategoryPress }) => {
  const { t } = useTranslation();

  return (
    <div className="quick-action-list">
      <Typography variant="subtitle2" className="quick-action-list__title">
        {t('task.ai.quickActions.thingsYouCanDo')}
      </Typography>
      {categories.map((category, index) => (
        <motion.button
          key={category.id}
          className="quick-action-list__item"
          onClick={() => onCategoryPress(category)}
          type="button"
          variants={fadeInDownVariants}
          initial="initial"
          animate="animate"
          transition={{
            duration: 0.28,
            delay: index * 0.06,
            ease: 'easeOut',
          }}
        >
          <div
            className="quick-action-list__icon-wrapper"
            style={{ backgroundColor: category.accentColor }}
          >
            {category.icon}
          </div>
          <div className="quick-action-list__copy-wrapper">
            <Typography variant="body1" className="quick-action-list__title">
              {t(`task.ai.quickActions.categories.${category.id}.title`, {
                defaultValue: category.title,
              })}
            </Typography>
            {category.description && (
              <Typography variant="subtitle1" className="quick-action-list__description">
                {t(`task.ai.quickActions.categories.${category.id}.description`, {
                  defaultValue: category.description,
                })}
              </Typography>
            )}
          </div>
          <ChevronRight className="quick-action-list__chevron" />
        </motion.button>
      ))}
    </div>
  );
};

export default QuickActionList;
